import _typeof from "@babel/runtime/helpers/typeof";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "id", "children", "appendTo"],
    _excluded2 = ["label", "children", "onClick", "isDisabled", "icon", "isNew"],
    _excluded3 = ["children", "title"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { forwardRef, useContext, Children, useState, cloneElement, useRef, createContext } from 'react';
import cx from 'classnames';
import { prefix, wrapEvent } from '../../tools';
import { Styled } from './ContextMenu.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var ContextMenuContext = createContext();

var useContextMenuContext = function useContextMenuContext() {
  var context = useContext(ContextMenuContext);

  if (context == null) {
    throw Error('useContextMenuContext must be used within <ContextMenu />');
  }

  return context;
};

var ContextMenu = forwardRef(function (_ref, ref) {
  var _cx;

  var customClassName = _ref.className,
      id = _ref.id,
      children = _ref.children,
      _ref$appendTo = _ref.appendTo,
      appendTo = _ref$appendTo === void 0 ? document.body : _ref$appendTo,
      rest = _objectWithoutProperties(_ref, _excluded);

  var instanceRef = useRef();
  var triggerRef = useRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      visible = _useState2[0],
      setVisible = _useState2[1];

  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "-context-menu"), true), _defineProperty(_cx, customClassName, !!customClassName), _cx));
  var context = {
    instanceRef: instanceRef,
    visible: visible,
    setVisible: setVisible
  };
  var renderTrigger;
  var renderContent;
  Children.map(children, function (el, index) {
    if (index === 1) {
      renderContent = el;
    }

    if (index === 0) {
      renderTrigger = cloneElement(el, {
        ref: triggerRef
      });
    }
  });
  return _jsx(ContextMenuContext.Provider, {
    value: context,
    children: _jsx(Styled.ContextMenu, _objectSpread(_objectSpread({
      onClickOutside: function onClickOutside() {
        return setVisible(false);
      },
      className: className,
      content: renderContent,
      visible: visible,
      duration: 150,
      role: "menu",
      offset: [0, 0],
      placement: "right-start",
      appendTo: appendTo,
      interactive: true,
      arrow: false,
      onCreate: function onCreate(instance) {
        return instanceRef.current = instance;
      },
      ignoreAttributes: true
    }, rest), {}, {
      children: renderTrigger
    }))
  });
});
var ContextMenuList = forwardRef(function (_ref2, ref) {
  var children = _ref2.children;
  return _jsx(Styled.ContextMenuList, {
    ref: ref,
    className: "".concat(prefix, "-context-menu__list"),
    children: children
  });
});
var ContextMenuTrigger = forwardRef(function (_ref3, ref) {
  var children = _ref3.children;

  var _useContextMenuContex = useContextMenuContext(),
      instanceRef = _useContextMenuContex.instanceRef,
      setVisible = _useContextMenuContex.setVisible;

  return _jsx("div", {
    onContextMenu: function onContextMenu(e) {
      e.persist();
      e.options = {
        passive: false
      };
      e.preventDefault();
      instanceRef.current.setProps({
        getReferenceClientRect: function getReferenceClientRect() {
          return {
            width: 0,
            height: 0,
            top: e.clientY,
            bottom: e.clientY,
            left: e.clientX,
            right: e.clientX
          };
        }
      });
      setVisible(true);
    },
    children: children
  });
});

var ContextMenuItem = function ContextMenuItem(_ref4) {
  var label = _ref4.label,
      children = _ref4.children,
      onClick = _ref4.onClick,
      isDisabled = _ref4.isDisabled,
      icon = _ref4.icon,
      isNew = _ref4.isNew,
      rest = _objectWithoutProperties(_ref4, _excluded2);

  var _useContextMenuContex2 = useContextMenuContext(),
      setVisible = _useContextMenuContex2.setVisible;

  var handleClick = wrapEvent(onClick, function () {
    return setVisible(false);
  });

  if (_typeof(children) === 'object') {
    return _jsx(Styled.ContextMenu, {
      content: _jsx(Styled.ContextMenuList, {
        children: children
      }),
      duration: 150,
      role: "menu",
      arrow: false,
      className: "nested",
      placement: "right-start",
      offset: [-16, -8],
      animation: "shift-away",
      interactive: true,
      ignoreAttributes: true,
      popperOptions: {
        modifiers: [{
          name: 'flip',
          options: {
            fallbackPlacements: ['left-start']
          }
        }]
      },
      children: _jsxs(Styled.ContextMenuItem, {
        role: "menuitem",
        "aria-disabled": isDisabled,
        isDisabled: isDisabled,
        children: [icon && _jsx(Styled.ContextMenuIcon, {
          icon: icon
        }), _jsx(Styled.ContextMenuLabel, {
          children: label
        }), _jsxs(Styled.ContextMenuExtra, {
          children: [isNew && _jsx(Styled.ContextMenuChip, {
            children: "New"
          }), _jsx(Styled.ContextMenuIconChevron, {})]
        })]
      })
    });
  } else {
    return _jsxs(Styled.ContextMenuItem, _objectSpread(_objectSpread({
      className: "".concat(prefix, "-menu__item"),
      role: "menuitem",
      onClick: handleClick,
      "aria-disabled": isDisabled,
      isDisabled: isDisabled
    }, rest), {}, {
      children: [icon && _jsx(Styled.ContextMenuIcon, {
        icon: icon
      }), _jsx(Styled.ContextMenuLabel, {
        children: label ? label : children
      }), _jsx(Styled.ContextMenuExtra, {
        children: isNew && _jsx(Styled.ContextMenuChip, {
          children: "New"
        })
      })]
    }));
  }
};

var ContextMenuGroup = forwardRef(function (_ref5, ref) {
  var children = _ref5.children,
      title = _ref5.title,
      rest = _objectWithoutProperties(_ref5, _excluded3);

  return _jsxs(Styled.ContextMenuGroup, {
    className: "".concat(prefix, "-context-menu__group"),
    ref: ref,
    role: "group",
    children: [title && _jsx("span", {
      className: "".concat(prefix, "-context-menu__group--name"),
      children: title
    }), children]
  });
});
var ContextMenuDivider = forwardRef(function (props, ref) {
  return _jsx(Styled.ContextMenuDivider, _objectSpread({
    ref: ref,
    className: "".concat(prefix, "-context-menu__divider"),
    orientation: "horizontal"
  }, props));
});
ContextMenu.Trigger = ContextMenuTrigger;
ContextMenu.List = ContextMenuList;
ContextMenu.Divider = ContextMenuDivider;
ContextMenu.Item = ContextMenuItem;
ContextMenu.Group = ContextMenuGroup;
ContextMenu.displayName = 'ContextMenu';
export default ContextMenu;